import { useEffect } from "react";

/**
 * Global single-key and chord ("G then I") shortcuts, Linear style.
 * Ignored while typing in inputs or when a modifier other than Shift is held.
 */
export type Shortcut = { keys: string; run: () => void };

const CHORD_TIMEOUT = 800;

export function useShortcuts(shortcuts: Shortcut[]) {
  useEffect(() => {
    let pending = "";
    let timer: ReturnType<typeof setTimeout> | undefined;

    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.defaultPrevented || e.isComposing) return;
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      // ⌘/Ctrl combos are declared as "Meta+x" and also work while typing.
      if (e.metaKey || e.ctrlKey) {
        const combo = shortcuts.find((s) => s.keys === `Meta+${e.key.toLowerCase()}`);
        if (combo) {
          e.preventDefault();
          combo.run();
        }
        return;
      }
      if (typing) return;
      // Keep native activation and navigation on focused controls. Row shortcuts
      // must not turn Enter on a property button into "open the issue".
      if (t?.closest("button, a, [role='button'], [role='menuitem'], [role='tab']") &&
          ["Enter", " ", "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
        pending = "";
        clearTimeout(timer);
        return;
      }
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const seq = pending ? `${pending} ${key}` : key;

      const exact = shortcuts.find((s) => s.keys === seq);
      if (exact) {
        e.preventDefault();
        pending = "";
        clearTimeout(timer);
        exact.run();
        return;
      }
      if (shortcuts.some((s) => s.keys.startsWith(seq + " "))) {
        pending = seq;
        clearTimeout(timer);
        timer = setTimeout(() => (pending = ""), CHORD_TIMEOUT);
        return;
      }
      pending = "";
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(timer);
    };
  }, [shortcuts]);
}
